import { useDeleteTag, useTagQuery } from '@/api/queries/tag';
import type { TagWithCount } from '@/api/types';
import { BinIcon } from '@/common/bin-icon';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { ConfirmationModal, ConfirmationModalType } from '@/common/confimation-modal';
import { CreateTagModal } from '@/common/create-tag-modal';
import { Modal } from '@/common/modal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState, useCallback, type FC } from 'react';

interface EditTagModalProps {
    onDone?: () => void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

export const EditTagModal: FC<EditTagModalProps> = ({ onDone, isOpen, setIsOpen }) => {
    const [tags, setTags] = useState<TagWithCount[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [addTagModalOpen, setAddTagModalOpen] = useState<boolean>(false);
    const [deleteTagModalOpen, setDeleteTagModalOpen] = useState<boolean>(false);
    const [selectedForDelete, setSelectedForDelete] = useState<number | null>(null);

    const { refetch: _ } = useTagQuery({
        onSuccess: (data: TagWithCount[]) => setTags(data),
        staleTime: Number.POSITIVE_INFINITY,
        cacheTime: 0,
    });

    const { mutate: deleteTag } = useDeleteTag();

    useEffect(() => {
        if (selectedForDelete !== null) {
            setDeleteTagModalOpen(true);
        }
    }, [selectedForDelete]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedForDelete(null);
        }
    }, [isOpen]);

    return (
        <div>
            <Modal isOpen={isOpen}>
                <div className="flex flex-col absolute min-w-64 w-[30vw] max-w-[650px] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                    <div className="flex flex-row justify-between p-4 items-center">
                        <p className="text-xl text-white">Edit Tags</p>
                        <XMarkIcon
                            className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                            onClick={isLoading ? () => {} : () => setIsOpen(false)}
                        />
                    </div>
                    <hr className="bg-gray-100 border-gray-120" />
                    <div className="flex flex-col">
                        <div className="flex flex-col max-h-[40vh] overflow-y-auto no-scrollbar">
                            {tags.map((tag, index) => {
                                const count = tag.count + tag.draftCount;
                                return (
                                    <div key={`${Date.now().toString()}${index}`} className="flex flex-col w-full">
                                        <div className="flex flex-row p-3 justify-between items-center w-full">
                                            <div className="flex flex-col">
                                                <p className="text-base text-white">{tag.name}</p>
                                                <p className="text-xs text-gray-50">
                                                    {count} Template{count === 1 ? '' : 's'}
                                                </p>
                                            </div>
                                            <button type="button" onClick={() => setSelectedForDelete(tag.id)}>
                                                <BinIcon className="stroke-gray-50 hover:stroke-red-50" />
                                            </button>
                                        </div>
                                        <hr className="bg-gray-100 border-gray-120 opacity-20 mt-1" />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="p-3">
                            <button
                                type="button"
                                className="group flex flex-row items-center gap-1.5 w-full py-2 text-blue-30 hover:text-blue-50 text-xs"
                                onClick={() => setAddTagModalOpen(true)}
                            >
                                <PlusIcon className="h-5 w-5 text-blue-30 group-hover:stroke-blue-50" />
                                Create New Tag
                            </button>
                        </div>
                        <hr className="mt-2 bg-gray-100 border-gray-120" />
                        <div className="w-full flex flex-row justify-end gap-3 p-6">
                            <Button
                                title="Cancel"
                                className="hover:bg-gray-160"
                                buttonType={ButtonType.SECONDARY}
                                buttonSize={ButtonSize.LARGE}
                                disabled={isLoading}
                                onClick={() => setIsOpen(false)}
                            />
                            <Button
                                title="Done"
                                buttonType={ButtonType.PRIMARY}
                                buttonSize={ButtonSize.LARGE}
                                disabled={isLoading}
                                onClick={() => {
                                    onDone?.();
                                    setIsOpen(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
            <CreateTagModal isOpen={addTagModalOpen} setIsOpen={setAddTagModalOpen} />
            <ConfirmationModal
                isOpen={deleteTagModalOpen}
                setIsOpen={setDeleteTagModalOpen}
                type={ConfirmationModalType.DEFAULT}
                confirmText="Delete Tag"
                cancelText="Cancel"
                message="Are you sure you want to permanently delete this tag?"
                subMessage="This tag will be removed off of all templates."
                onConfirm={() => {
                    deleteTag(selectedForDelete ?? -1);
                }}
            />
        </div>
    );
};
